import { Link } from '@solidjs/router';
import clsx from 'clsx';
import { Show, onCleanup, onMount, useContext } from 'solid-js';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { Grid } from '../../../grid-system/grid/grid';
import theme from '../../../style/theme';
import { ErrorCatcher } from '../../../tools/error-catcher';
import { NewAtosCareLogo } from '../../icons-library/new-atos-care-logo';
import { CalendarIcon } from '../../icons-library/calendar';
import { GET_PRIMARY_MENU_QUERY } from '../../menu/menu';
import { PrimaryMenuData } from '../../menu/menu-types';
import { HeaderDropdownMenu } from '../header-dropdown-menu';
import { headerNavItemClasses, publishHeaderHeight } from '../shared';
import { HeaderAtosCareNavigationShortcuts } from './header-atos-care-navigation-shortcuts';
import { SearchPanel } from '../../search/search-panel';
import urlMatches from '../../../tools/url-match';
import removeAmpersand from '../../../tools/remove-ampersand';

export const HeaderAtosCareContainer = (props: { isHcp: boolean }) => {
    const { createCachedResource, viewport, blogsNewsListingPage, eventsListingPage } = useContext(AppContext);

    const menuLocation = props.isHcp ? 'primary_menu_hcp' : 'primary_menu';

    const [primaryMenuContent] = createCachedResource(GET_PRIMARY_MENU_QUERY, () => ({ key: `/wp-json/content/v1/menus/${menuLocation}` }), true);
    const primaryMenuData = (): PrimaryMenuData => primaryMenuContent()?.menu?.content;

    const isOriginalTablet = () => viewport.width <= theme.breakpoints.ORIGINAL_TABLET;

    let headerRef: HTMLElement | undefined;

    onMount(() => {
        if (headerRef) {
            onCleanup(publishHeaderHeight(headerRef));
        }
    });

    return (
        <ErrorCatcher componentName="Header">
            <header ref={headerRef} id="site-header" class="[container-type:inline-size] sticky z-[3] bg-cvi-bg-body top-0 print:hidden">
                <Grid templateShorthand={[12]} isLayoutGrid={true}>
                    <div class="flex flex-col relative z-[3]">
                        {/* Top row — logo left, shortcuts right. The nav row below spans the full
                        banner width so it starts at the left edge, like the main-site header. */}
                        <div class={clsx('flex justify-between items-center gap-cvi-2', isOriginalTablet() ? 'py-cvi-5' : 'py-cvi-6')}>
                            <Link
                                href="/"
                                class={clsx(
                                    'block shrink-0',
                                    isOriginalTablet() ? 'w-cvi-logo-sm-w h-cvi-logo-sm-h' : 'w-cvi-logo-lg-w h-cvi-logo-lg-h'
                                )}
                            >
                                <NewAtosCareLogo width="100%" height="100%" />
                            </Link>

                            <HeaderAtosCareNavigationShortcuts />
                        </div>

                        <Show when={!isOriginalTablet()}>
                            <div class="flex justify-between items-start gap-cvi-11 w-full pb-cvi-5">
                                <HeaderDropdownMenu primaryMenuData={primaryMenuData()} />

                                <nav class="flex">
                                    <Show when={blogsNewsListingPage.url}>
                                        <Link href={blogsNewsListingPage.url}>
                                            <div class={clsx(
                                                headerNavItemClasses,
                                                'flex justify-center items-center px-cvi-6 py-cvi-5 cursor-pointer whitespace-nowrap',
                                                urlMatches(blogsNewsListingPage.url) ? 'font-cvi-bold' : 'font-cvi-medium'
                                            )}>
                                                <span>{removeAmpersand(blogsNewsListingPage.title)}</span>
                                            </div>
                                        </Link>
                                    </Show>
                                    <Show when={eventsListingPage?.url}>
                                        <Link href={eventsListingPage.url}>
                                            <div class={clsx(
                                                headerNavItemClasses,
                                                'flex justify-center items-center gap-cvi-2 px-cvi-6 py-cvi-5 cursor-pointer whitespace-nowrap',
                                                urlMatches(eventsListingPage.url) ? 'font-cvi-bold' : 'font-cvi-medium'
                                            )}>
                                                <CalendarIcon size="medium" fill="currentColor" />
                                                <span>{removeAmpersand(eventsListingPage.title)}</span>
                                            </div>
                                        </Link>
                                    </Show>
                                </nav>
                            </div>
                        </Show>
                    </div>
                </Grid>

                {/* Hangs off the header's bottom edge; see search-panel.tsx. */}
                <SearchPanel />
            </header>
        </ErrorCatcher>
    );
};
